import { createNullCache } from '@algolia/cache-common';
import { createInMemoryCache } from '@algolia/cache-in-memory';
import algolia from 'algoliasearch';
import BluebirdPromise from 'bluebird';
import { isEqual, isUndefined } from 'lodash';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import Async from 'react-select/async';
import { RuntimeContext } from '../../../common/RuntimeContext';

export default class SearchSelect extends PureComponent {
    constructor(props) {
        super(props);
        this.state = { valueObject: null };
    }

    static propTypes = {
        defaultFilters: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        attributeList: PropTypes.array,
        restrictSearchableAttributes: PropTypes.array,
        formOptions: PropTypes.func,
        indexName: PropTypes.string,
        className: PropTypes.string,
        isMulti: PropTypes.bool,
        handleSelect: PropTypes.func,
        forceClear: PropTypes.bool,
        input: PropTypes.object,
        initialValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        placeholder: PropTypes.string,
        disabled: PropTypes.bool,
        initializeSearch: PropTypes.bool,
        allowNull: PropTypes.bool,
        hitsPerPage: PropTypes.number,
        sortBy: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        disableCache: PropTypes.bool,
        searchKey:PropTypes.string,
    };

    static contextType = RuntimeContext;

    getClient = (disableCache) => {
        const options = disableCache
            ? { requestsCache: createNullCache(), responsesCache: createNullCache() }
            : { requestsCache: createInMemoryCache(), responsesCache: createInMemoryCache() };
        return algolia(this.context.pathsWeb.algoliaAppID, this.props.searchKey, options);
    };

    componentDidMount() {
        const client = this.getClient(this.props.disableCache);
        this.index = client.initIndex(this.props.indexName);

        const { initialValue, input = {}, initializeSearch } = this.props;
        if (initialValue) {
            this.setState({ valueObject: initialValue });
        }
        if (initializeSearch) {
            const searchValue = input.value || ' ';
            this.loadOptions(searchValue).then((options) => {
                options.forEach((item) => {
                    if (item.value === input.value) {
                        this.setState({ valueObject: item });
                    }
                });
            });
        }
    }

    componentDidUpdate(prevProps) {
        const { initialValue, input } = this.props;
        if (
            (this.props.forceClear && !prevProps.forceClear) ||
            (prevProps.input && prevProps.input.value && !input.value)
        ) {
            this.setState({ valueObject: null });
        }

        if (!isEqual(prevProps.initialValue, initialValue)) {
            this.setState({ valueObject: initialValue });
        }
    }

    handleBlur = () => {
        if (this.props.input && this.props.input.onBlur && this.state.valueObject) {
            this.props.input.onBlur(this.state.valueObject.value, this.props.input.name);
        }
    };

    loadOptions = (values) => {
        const {
            defaultFilters,
            attributeList,
            formOptions,
            isMulti,
            initializeSearch,
            hitsPerPage = 10,
            sortBy = '',
            restrictSearchableAttributes
        } = this.props;

        const { valueObject } = this.state;
        let searchResultPromise = BluebirdPromise.resolve();
        let searchOptions;
        if (values !== '' || initializeSearch) {
            searchOptions = {
                query: values,
                attributesToRetrieve: attributeList,
                hitsPerPage // algolia has hard limit of 1000
            };

            if (defaultFilters) {
                searchOptions.filters = defaultFilters;
            }
            if (restrictSearchableAttributes) {
                searchOptions.restrictSearchableAttributes = restrictSearchableAttributes;
            }

            searchResultPromise = this.index.search(values, searchOptions);
        }

        return searchResultPromise.then((res) => {
            if (res) {
                const results = res.hits;

                if (sortBy) {
                    // algolia discourages sorting in index so sorting here instead
                    results.sort(
                        typeof sortBy === `function`
                            ? sortBy
                            : (a, b) => (a[sortBy]?.toUpperCase() > b[sortBy]?.toUpperCase() ? 1 : -1)
                    );
                }

                const options = formOptions(results) || [];

                if (isMulti && valueObject && valueObject.length) {
                    return [...options, ...valueObject];
                }

                return options;
            }
            return [];
        });
    };

    onInputChange = (item) => {
        const { handleSelect, allowNull = false } = this.props;
        if (item && handleSelect) {
            this.setState({ valueObject: item });
            handleSelect(item);
        } else if (item) {
            this.setState({ valueObject: item });
        } else {
            this.setState({ valueObject: null });
            if (allowNull && handleSelect && !isUndefined(item)) {
                handleSelect(item);
            }
        }
        if (this.props.input && this.props.input.onChange) {
            if (item) {
                this.props.input.onChange(item.value);
            } else {
                this.props.input.onChange(item);
            }
        }
    };

    render() {
        const {
            className,
            isMulti = false,
            placeholder,
            input,
            disabled = false,
            initializeSearch,
            ...rest
        } = this.props;
        const { valueObject } = this.state;
        return (
            <Async
                {...input}
                {...rest}
                loadOptions={this.loadOptions}
                defaultOptions={initializeSearch}
                isClearable={true}
                className={`search-select verb-select ${className}`}
                value={valueObject}
                isMulti={isMulti}
                placeholder={placeholder}
                onChange={this.onInputChange}
                onBlur={this.handleBlur}
                isDisabled={disabled}
                classNamePrefix={'verb-select'}
            />
        );
    }
}
